import { IChannel, ITriggerType } from '.';

export type ConversationHistoryEntry = {
  Content: string;
  Inbound: boolean;
  Timestamp: string; // ISO 8601 format date string
};

export type UpcomingMessage = {
  Content: string;
  Inbound: boolean;
  Timestamp: string; // ISO 8601 format date string
};

export enum MessageStatus {
  SENDING = 'sending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
}

export type MessageFeedback = {
  FeedbackContent: string;
  isSatisfactory: boolean;
};

/** Something the AI pipeline decided or dropped that produced no message. */
export type ConversationEventType =
  | 'followups_not_generated'
  | 'followup_blocked'
  | 'followup_dropped_duplicate';

export type ConversationEvent = {
  EventID: string;
  Type: ConversationEventType | string;
  /** Where the event sits in the timeline: when the message would have gone out. */
  Timestamp: string;
  /** When the decision was taken. */
  CreatedAt: string;
  /** Human-readable reason, e.g. the one the AI gave for not following up. */
  Detail?: string | null;
  /** The dropped message, when there was one. */
  RelatedContent?: string | null;
};

export type MessageEntry = {
  Content: string;
  Inbound: boolean;
  Timestamp: string; // ISO 8601 format date string
  IsDeleted?: boolean;
  ExternalMessageId?: string;
  MessageId?: string;
  Status?: MessageStatus;
  StatusError?: string;
  Feedback?: MessageFeedback;
  Type?: ITriggerType;
  SentBy?: 'ai' | 'user'; // null/undefined treated as 'ai' for backward compat
  Buttons?: { Title: string; URL: string }[] | null; // URL buttons for Instagram Generic Template
  QuickReplies?: { Title: string; Payload: string }[] | null; // Quick-reply chips for Instagram
  MediaType?: 'audio' | null; // "audio" for voice messages
  AudioUrl?: string | null; // Relative URL for voice message audio playback
  ScheduledMessageID?: string | null; // Present only for upcoming scheduled messages
  PendingApproval?: boolean | null; // True when message requires manual approval
  ContentUnavailable?: boolean | null; // Failed message whose body could no longer be resolved
  ClientMessageId?: string; // Transient, frontend-only: correlates an in-flight manual send with its ack/error
};

export type RecommendedActionType =
  | 'call_back'
  | 'follow_up'
  | 'send_booking_link'
  | 'send_pricing'
  | 'send_information'
  | 'answer_question'
  | 'update_crm'
  | 'set_reminder'
  | 'mark_qualified'
  | 'mark_not_qualified'
  | 'other';

// Next best action suggested to the salesperson along with the summary
export type RecommendedAction = {
  Type: RecommendedActionType;
  Title: string;
  Reason?: string | null;
  Priority: 'high' | 'medium' | 'low';
  /** When to do it, only when it matters (e.g. "today"). */
  Timing?: string | null;
};

// AI recap of a conversation, generated on demand from the inbox
export type ConversationSummary = {
  Overview: string;
  CurrentStatus?: string | null;
  InterestLevel: 'high' | 'medium' | 'low' | 'unknown';
  Needs: string[];
  Budget?: string | null;
  Timeline?: string | null;
  QualificationAnswers: string[];
  Objections: string[];
  OpenQuestions: string[];
  ImportantNotes: string[];
  NextStep?: string | null;
  /** Most important first; absent on summaries generated before actions existed. */
  RecommendedActions?: RecommendedAction[];
  Language?: string | null;
  GeneratedAt: string;
  /** Messages the summary covers; fewer than the conversation has means it is outdated. */
  MessageCount: number;
};

// Why the channel this conversation runs on cannot send right now
export type SendingBlockedInfo = {
  Reason: 'whatsapp_phone_disconnected' | 'whatsapp_phone_paused';
  PhoneNumber?: string | null;
  Detail?: string | null;
};

export type LeadInformation = {
  LeadID: string;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  EmailAddress: string | null;
  CreatedAt: string; // ISO 8601 format date string
  LeadIntegration: string;
  OrgId: string;
  ProfilePictureURL?: string;
  Username?: string;
  BusinessWhatsappPhoneNumber?: string;
  BusinessInstagramUsername?: string;
  AssignedToUserId?: string;
  IsPersonalWhatsapp?: boolean;
  /** The sequence that enrolled this lead (its follow-ups and agent drive the conversation). */
  SequenceID?: string | null;
  /** Null when the sequence has been deleted. */
  SequenceName?: string | null;
  /** False when the sequence is paused (the AI then stops replying to this lead). */
  SequenceActive?: boolean | null;
};

export type ConversationData = {
  Classification: string;
  ConversationHistory: MessageEntry[];
  UpcomingMessages: MessageEntry[];
  LeadInformation: LeadInformation;
  IsActive: boolean;
  /** This conversation's override of the org manual-reply preference; null follows the org. */
  KeepAgentOnManualReply?: boolean | null;
  Channel: IChannel;
  AgentName?: string;
  SendingBlocked?: SendingBlockedInfo | null;
  Events?: ConversationEvent[];
  Summary?: ConversationSummary | null;
  /** ISO 639-1 code of the language the lead writes in, once detected. */
  LeadLanguage?: string | null;
  TriggerDetails?: {
    TriggerType: string;
    TextContent?: string;
    ExternalID: string;
    MediaURL?: string;
    ExternalURL?: string;
    SourceMediaId?: string;
    Timestamp: string;
  };
};

export enum Classification {
  ALL = 'all',
  UNDETERMINED = 'undetermined',
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  ENGAGED = 'engaged',
  WAITING_AI_RESPONSE = 'waiting_ai_response',
}

export type CustomClassification = {
  id: string;
  label: string;
  color: string;
  text_color: string;
  description?: string | null;
  ai_enabled?: boolean;
  agent_policy?: string | null; // null=neutral, "enabled", "disabled"
};

export type ConversationPreview = {
  LeadID: string;
  Channel: IChannel;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  LatestMessage: MessageEntry;
  Classification: string;
  CreatedAt: string; // ISO 8601 format date string
  Username?: string;
  ProfilePictureURL?: string;
  BusinessWhatsappPhoneNumber?: string;
  BusinessInstagramUsername?: string;
  ProfileLastUpdatedAt?: string; // ISO 8601 format date string
  HasUpcomingMessages?: boolean;
  AssignedToUserId?: string;
  AgentName?: string;
};

export type ToggleConversationAIRequest = {
  EnableAI: boolean;
};
